import { Link } from "@tanstack/react-router";
import { useDocumentMeta } from "@/lib/useDocumentMeta";

/**
 * Global 404 — wired as the root route's `notFoundComponent`, so any
 * URL that matches no registered route renders this instead of
 * TanStack Router's bare error boundary.
 *
 * Recovery links stay deliberately public-only (`/` and `/teams`):
 * a 404 is reachable anonymously, so the shell must not assume a
 * signed-in user. A signed-in viewer can still reach their own
 * surfaces from the persistent `<PublicAppHeader>` rendered by
 * `RootLayout` above this outlet.
 */
export function NotFoundPage() {
  useDocumentMeta({
    title: "Page not found · tktic",
    description: "This page doesn’t exist.",
    ogType: "website",
  });

  return (
    <main
      data-testid="not-found-page"
      className="min-h-[60vh] bg-canvas text-ink flex flex-col items-center justify-center gap-3 p-6 text-center"
    >
      <p className="font-mono text-xs uppercase tracking-wider text-accent">
        Error 404
      </p>
      <h1 className="font-display text-3xl tracking-tight text-ink sm:text-4xl">
        Page not found
      </h1>
      <p className="font-body text-sm text-ink-secondary max-w-md">
        The page you’re looking for doesn’t exist, or the link that
        brought you here is out of date.
      </p>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/"
          data-testid="not-found-home-link"
          className="font-display text-sm font-semibold text-ink underline decoration-accent decoration-2 underline-offset-4 hover:decoration-[3px]"
        >
          ← Back to home
        </Link>
        <Link
          to="/teams"
          data-testid="not-found-teams-link"
          className="font-mono text-xs uppercase tracking-wider text-ink-muted hover:text-accent"
        >
          Browse teams
        </Link>
      </div>
    </main>
  );
}
